import React from "react";
import {
  Box, Flex, Heading, HStack, SimpleGrid, Spinner, Stack, Text,
} from "@chakra-ui/react";
import {
  FiArrowRight, FiCheck, FiCompass, FiMapPin, FiUsers,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import RedirectStatusPage from "../../main/Status/RedirectStatusPage";
import { usePerfilContext } from "features/perfil/context/hooks";
import { useMisHijos } from "features/perfil/hijos/hooks";
import { usePerfilRegistroContext } from "features/perfil/registro/hooks";

function completeParent(name, apellidoPaterno, apellidoMaterno, email, phone) {
  return Boolean(name?.trim() && apellidoPaterno?.trim() && apellidoMaterno?.trim() && email?.trim() && phone?.trim());
}
function firstName(value) {
  return String(value || "Familia").trim().split(/\s+/)[0];
}

function QuickLink({ to, icon, title, detail, color }) {
  return <Box asChild><Link to={to}><HStack bg="design.bg.surface" p={{ base: "4", lg: "5" }} minH={{ lg: "82px" }} rounded="20px" borderWidth="1px" borderColor="design.border.subtle" gap="3" transition=".15s" _hover={{ borderColor: `${color}.400`, transform: "translateY(-1px)", boxShadow: "md" }}>
    <Flex boxSize="42px" rounded="15px" bg={`${color}.50`} color={`${color}.600`} _dark={{ bg: "design.bg.muted" }} align="center" justify="center">{icon}</Flex>
    <Box flex="1"><Text fontWeight="800">{title}</Text><Text fontSize="xs" color="design.fg.subtle">{detail}</Text></Box><Box color="design.fg.subtle"><FiArrowRight/></Box>
  </HStack></Link></Box>;
}

export default function InicioPerfil() {
  const { data: context, isLoading: contextLoading } = usePerfilContext();
  const { data: children = [], isLoading: childrenLoading } = useMisHijos();
  const { data: registration = {}, isLoading: registrationLoading } = usePerfilRegistroContext();
  const family = context?.familia;
  const socios = registration.socios || [];
  const loading = contextLoading || childrenLoading || registrationLoading;
  const parentsReady = family
    && completeParent(family.nombreMama, family.apellidoPaternoMama, family.apellidoMaternoMama, family.correoMama, family.telefonoMama)
    && completeParent(family.nombrePapa, family.apellidoPaternoPapa, family.apellidoMaternoPapa, family.correoPapa, family.telefonoPapa);
  const childrenReady = children.length > 0 && children.every((child) => child.nombre?.trim() && child.apellidoPaterno?.trim() && child.apellidoMaterno?.trim());
  const schoolReady = Boolean(family?.campusId && family?.colegio?.trim());
  const registrationsReady = socios.length > 0 && socios.every((socio) => socio.club?.registered || socio.camp?.registered);

  if (loading) return <Flex minH="55vh" align="center" justify="center"><Spinner color="brand.red"/></Flex>;
  if (!parentsReady || !childrenReady || !schoolReady || !registrationsReady) return <RedirectStatusPage to="/perfil/onboarding" eyebrow="Completemos tu perfil" title="Falta un poco de información" description="Antes de continuar, necesitamos terminar los datos y registros de tu familia." actionLabel="Continuar registro" />;

  const campus = children[0]?.campusNombre || "Campus Arawaks";
  const greeting = firstName(family.nombreMama || family.nombrePapa);
  return <Stack gap={{ base: "7", lg: "6" }} pb="5" maxW="1120px" mx="auto">
    <Box display={{ base: "none", lg: "block" }}><Text color="design.fg.muted" fontSize="sm" fontWeight="700">Mi perfil</Text><Heading mt="1" fontSize="2xl">Resumen familiar</Heading></Box>
    <SimpleGrid columns={{ base: 1, lg: 2 }} gap={{ base: "7", lg: "6" }} alignItems="stretch">
    <Box p={{ base: "6", md: "7", lg: "8" }} minH={{ lg: "292px" }} rounded="28px" color="white" bg="linear-gradient(135deg, #7f1d1d 0%, #b91c1c 55%, #dc2626 100%)" position="relative" overflow="hidden">
      <Box position="absolute" boxSize="190px" rounded="full" bg="red.400" opacity=".32" right="-55px" top="-85px"/>
      <Box position="absolute" boxSize="95px" rounded="full" bg="orange.300" opacity=".22" right="105px" bottom="-55px"/>
      <Box position="absolute" boxSize="52px" rounded="full" bg="white" opacity=".1" left="-15px" top="18px"/>
      <HStack position="relative" justify="space-between" align="start" gap="4" h="full">
        <Box><Text color="red.100" fontSize="sm" fontWeight="800">Resumen familiar</Text><Heading mt="1" fontSize={{ base: "3xl", md: "4xl" }}>{`Hola, ${greeting}`}</Heading><HStack mt="3" color="red.100"><FiMapPin/><Text fontSize="sm">{campus}</Text></HStack></Box>
        <Flex boxSize="56px" rounded="20px" bg="whiteAlpha.200" align="center" justify="center"><FiUsers/></Flex>
      </HStack>
    </Box>

    <Box p={{ lg: "6" }} rounded={{ lg: "28px" }} bg={{ lg: "design.bg.panel" }} borderWidth={{ lg: "1px" }} borderColor="design.border.subtle">
      <Heading fontSize="xl" mb="4">Administrar</Heading>
      <Stack gap="3">
        <QuickLink to="/perfil/familia" icon={<FiUsers/>} title="Familia" detail={`${children.length} hijos y 2 contactos`} color="red"/>
        <QuickLink to="/perfil/registro" icon={<FiCompass/>} title="Registros" detail="Club y Camp" color="blue"/>
      </Stack>
    </Box>
    </SimpleGrid>

    <Box p="5" rounded="22px" bg="green.50" color="green.800" borderWidth="1px" borderColor="green.100" _dark={{ bg: "green.950", color: "green.200", borderColor: "green.900" }}><HStack><Flex boxSize="38px" rounded="full" bg="green.500" color="white" align="center" justify="center"><FiCheck/></Flex><Box><Text fontWeight="800">Perfil completo</Text><Text fontSize="sm">{"La información principal de tu familia está actualizada."}</Text></Box></HStack></Box>
  </Stack>;
}
